import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { HOME_SECURITY_SLIDES } from './data';
import { EntranceHeadline } from './entrance_headline';
import {
  BodyMd,
  HeadingH2,
  HeadingH3,
  Section,
  SectionContainer,
} from './styles';
import { MEDIA, TOKENS } from './tokens';
import { useAutoCarousel } from './use_auto_carousel';

const CYCLE_MS = 4000;
const FIRST_CYCLE_MS = 6000;
const PANEL_TRANSITION_MS = 500;
const COPY_FADE_OUT_MS = 180;
const COPY_FADE_IN_MS = 400;

/** Slide 2 preview matches Figma: woman-focused tall crop; include her right hand. */
const SECURITY_IMAGE_POSITIONS = [
  { active: 'center center', inactive: 'center center' },
  { active: '40% center', inactive: '30% 45%' },
  { active: 'center center', inactive: 'center center' },
] as const;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${TOKENS.space[24]};
`;

const CarouselDots = styled.div`
  display: none;
  align-items: center;
  gap: ${TOKENS.space[8]};
  border-radius: ${TOKENS.radius.pill};
  padding: ${TOKENS.space[12]} ${TOKENS.space[16]};

  ${MEDIA.lg} {
    display: flex;
  }
`;

const CarouselDot = styled.button<{ $active?: boolean }>`
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: ${TOKENS.radius.pill};
  background: ${TOKENS.color.overlayScrim};
  width: ${({ $active }) => ($active ? '64px' : '8px')};
  height: 8px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 1;
  }
`;

const CarouselProgress = styled.span<{ $paused?: boolean }>`
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: ${TOKENS.color.iconDark};
  animation: security-progress ${CYCLE_MS}ms linear forwards;
  width: 100%;

  ${({ $paused }) => $paused && 'animation-play-state: paused;'}

  @keyframes security-progress {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const MOBILE_CAROUSEL_GAP_PX = 8;
/**
 * Space reserved outside the active card. Card = 100% − inset; each side
 * peeks ≈ inset/2 − gap/2 (~24px). Padding lets first/last slides center the
 * same way as the middle slide, so the right peek isn’t oversized on slide 1.
 */
const MOBILE_CARD_SIDE_INSET_PX = 64;
const MOBILE_CARD_PEEK_PX = MOBILE_CARD_SIDE_INSET_PX / 2;

const MobileCarousel = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: ${MOBILE_CARD_PEEK_PX}px;
  gap: ${MOBILE_CAROUSEL_GAP_PX}px;
  /* Match middle-slide peeks on first/last (without this, slide 1 clamps left
     and shows a much wider next-card peek). */
  padding-inline: ${MOBILE_CARD_PEEK_PX}px;
  margin-top: ${TOKENS.space[40]};
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${MEDIA.lg} {
    display: none;
  }
`;

const MobileCard = styled.article`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 280px;
  overflow: hidden;
  border-radius: ${TOKENS.radius.md};
  background: ${TOKENS.color.background};

  img {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;

const MobileCarouselCard = styled(MobileCard)`
  /* 100% of the padded content box ≡ viewport − SIDE_INSET */
  flex: 0 0 100%;
  scroll-snap-align: center;
`;

const MobileCarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: ${TOKENS.space[8]};
  margin-top: ${TOKENS.space[16]};

  ${MEDIA.lg} {
    display: none;
  }
`;

const MobileCarouselDot = styled.button<{ $active?: boolean }>`
  border: none;
  padding: 0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: ${({ $active }) => ($active ? '32px' : '8px')};
  height: 8px;
  border-radius: ${TOKENS.radius.pill};
  background: ${TOKENS.color.overlayScrim};
  transition: width 0.25s ease;
  flex-shrink: 0;
`;

const MobileCarouselProgress = styled.span<{ $paused?: boolean }>`
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: ${TOKENS.color.iconDark};
  animation: security-progress ${CYCLE_MS}ms linear forwards;
  width: 100%;

  ${({ $paused }) => $paused && 'animation-play-state: paused;'}
`;

const CardGradient = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  transition: background 0.25s ease;
  /* Active: copy scrim. Off-slide: heavy shadow so only a dim sliver reads (Figma). */
  background: ${({ $active }) =>
    $active
      ? `linear-gradient(
          0deg,
          ${TOKENS.color.neutral950_85} 0%,
          ${TOKENS.color.neutral950_40} 35%,
          transparent 70%
        )`
      : TOKENS.color.neutral950_64};
`;

const CardCopy = styled.div<{ $active: boolean }>`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  max-width: 380px;
  color: ${TOKENS.color.inverse};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease;
`;

const CardBodySm = styled.span`
  display: block;
  margin-top: ${TOKENS.space[4]};
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodySmSize};
  line-height: ${TOKENS.type.bodySmLineHeight};
  font-weight: ${TOKENS.type.bodySmWeight};
  color: ${TOKENS.color.onDark};
`;

const DesktopPanels = styled.div`
  display: none;
  margin-top: ${TOKENS.space[48]};
  gap: ${TOKENS.space[16]};

  ${MEDIA.lg} {
    display: flex;
  }
`;

const Panel = styled.button<{ $active: boolean }>`
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: none;
  border-radius: ${TOKENS.radius.md};
  background: ${TOKENS.color.background};
  text-align: left;
  cursor: pointer;
  height: 560px;
  flex: ${({ $active }) => ($active ? '5 1 0%' : '1.5 1 0%')};
  transition: flex ${PANEL_TRANSITION_MS}ms ease;
`;

const PanelImage = styled.img<{ $active: boolean; $slideIndex: number }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $active, $slideIndex }) =>
    $active
      ? SECURITY_IMAGE_POSITIONS[$slideIndex].active
      : SECURITY_IMAGE_POSITIONS[$slideIndex].inactive};
  transition: object-position ${PANEL_TRANSITION_MS}ms ease;
  transform: translateZ(0);
`;

const PanelScrim = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  background: ${({ $active }) => {
    if ($active) {
      return 'transparent';
    }

    /* Partial bottom gradient — matches Figma collapsed panels */
    return `linear-gradient(
      180deg,
      rgba(4, 7, 17, 0) 0%,
      rgba(4, 7, 17, 0.15) 40%,
      rgba(4, 7, 17, 0.55) 100%
    )`;
  }};
  transition: background ${PANEL_TRANSITION_MS}ms ease;
  pointer-events: none;
`;

const PanelGradient = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: auto 0 0;
  height: 317px;
  background: linear-gradient(
    0deg,
    ${TOKENS.color.neutral950_85} 0%,
    transparent 100%
  );
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity
    ${({ $visible }) => ($visible ? COPY_FADE_IN_MS : COPY_FADE_OUT_MS)}ms ease;
  pointer-events: none;
`;

const PanelCopy = styled.div<{ $visible: boolean; $collapsing: boolean }>`
  position: absolute;
  left: 24px;
  bottom: 24px;
  max-width: 460px;
  width: ${({ $collapsing }) => ($collapsing ? '460px' : 'auto')};
  right: ${({ $collapsing }) => ($collapsing ? 'auto' : '24px')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '8px')});
  transition:
    opacity
      ${({ $visible }) => ($visible ? COPY_FADE_IN_MS : COPY_FADE_OUT_MS)}ms
      ease,
    transform
      ${({ $visible }) => ($visible ? COPY_FADE_IN_MS : COPY_FADE_OUT_MS)}ms
      ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  overflow: hidden;
`;

const SecurityImage: React.FC<{
  image: string;
  alt: string;
}> = ({ image, alt }) => <img src={image} alt={alt} />;

export const HomeSecuritySection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const mobileCarouselRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [visibleCopyIndex, setVisibleCopyIndex] = useState(0);
  const [copyShown, setCopyShown] = useState(true);
  const copyTimersRef = useRef<{ panel?: number; fadeIn?: number }>({});

  const clearCopyTimers = () => {
    if (copyTimersRef.current.panel !== undefined) {
      window.clearTimeout(copyTimersRef.current.panel);
    }
    if (copyTimersRef.current.fadeIn !== undefined) {
      window.clearTimeout(copyTimersRef.current.fadeIn);
    }
    copyTimersRef.current = {};
  };

  const {
    activeIndex,
    isPaused,
    progressKey,
    goTo,
    pause,
    resume,
    intervalMs,
  } = useAutoCarousel(
    HOME_SECURITY_SLIDES.length,
    hasEnteredView,
    5000,
    0,
    FIRST_CYCLE_MS,
  );
  const prevActiveIndexRef = useRef(activeIndex);

  useEffect(() => {
    if (prevActiveIndexRef.current === activeIndex) {
      return undefined;
    }

    prevActiveIndexRef.current = activeIndex;
    clearCopyTimers();
    setCopyShown(false);

    copyTimersRef.current.panel = window.setTimeout(() => {
      setVisibleCopyIndex(activeIndex);
      copyTimersRef.current.fadeIn = window.setTimeout(() => {
        setCopyShown(true);
      }, 32);
    }, PANEL_TRANSITION_MS);

    return clearCopyTimers;
  }, [activeIndex]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const carousel = mobileCarouselRef.current;
    if (!carousel) return;

    const cards = carousel.querySelectorAll('article');
    const card = cards[activeIndex] as HTMLElement | undefined;
    if (!card) return;

    // Center the active card so left/right peeks stay equal (Figma slide 2).
    const targetLeft =
      card.offsetLeft + card.offsetWidth / 2 - carousel.clientWidth / 2;

    isProgrammaticScrollRef.current = true;
    carousel.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: hasEnteredView ? 'smooth' : 'auto',
    });

    const timer = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 350);

    return () => window.clearTimeout(timer);
  }, [activeIndex, hasEnteredView]);

  useEffect(() => {
    const carousel = mobileCarouselRef.current;
    if (!carousel) return;

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const cards = carousel.querySelectorAll('article');
      if (cards.length === 0) return;

      const viewportCenter = carousel.scrollLeft + carousel.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((node, index) => {
        const el = node as HTMLElement;
        const cardCenter = el.offsetLeft + el.offsetWidth / 2;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex) {
        goTo(closestIndex);
      }
    };

    carousel.addEventListener('scroll', onScroll, { passive: true });
    return () => carousel.removeEventListener('scroll', onScroll);
  }, [activeIndex, goTo]);

  return (
    <Section ref={sectionRef} id="security" $variant="security" $muted>
      <SectionContainer>
        <HeaderRow>
          <EntranceHeadline as={HeadingH2}>
            Safe, monitored and protected
          </EntranceHeadline>
          <CarouselDots role="group" aria-label="Security carousel">
            {HOME_SECURITY_SLIDES.map((slide, index) => (
              <CarouselDot
                key={slide.title}
                type="button"
                aria-label={
                  index === activeIndex
                    ? `${slide.title}, playing`
                    : `Go to ${slide.title}`
                }
                aria-current={index === activeIndex ? 'true' : undefined}
                $active={index === activeIndex}
                onClick={() => goTo(index)}
              >
                {index === activeIndex && (
                  <CarouselProgress
                    key={progressKey}
                    $paused={isPaused}
                    style={{ animationDuration: `${intervalMs}ms` }}
                  />
                )}
              </CarouselDot>
            ))}
          </CarouselDots>
        </HeaderRow>

        <MobileCarousel ref={mobileCarouselRef}>
          {HOME_SECURITY_SLIDES.map((slide, index) => {
            const active = index === activeIndex;
            return (
              <MobileCarouselCard key={slide.title}>
                <SecurityImage image={slide.image} alt={slide.imageAlt} />
                <CardGradient $active={active} />
                <CardCopy $active={active}>
                  <HeadingH3 $onDark>{slide.title}</HeadingH3>
                  <CardBodySm>{slide.description}</CardBodySm>
                </CardCopy>
              </MobileCarouselCard>
            );
          })}
        </MobileCarousel>
        <MobileCarouselDots role="group" aria-label="Security carousel">
          {HOME_SECURITY_SLIDES.map((slide, index) => (
            <MobileCarouselDot
              key={slide.title}
              type="button"
              aria-label={
                index === activeIndex
                  ? `${slide.title}, playing`
                  : `Go to ${slide.title}`
              }
              aria-current={index === activeIndex ? 'true' : undefined}
              $active={index === activeIndex}
              onClick={() => goTo(index)}
            >
              {index === activeIndex && (
                <MobileCarouselProgress
                  key={progressKey}
                  $paused={isPaused}
                  style={{ animationDuration: `${intervalMs}ms` }}
                />
              )}
            </MobileCarouselDot>
          ))}
        </MobileCarouselDots>

        <DesktopPanels
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              resume();
            }
          }}
        >
          {HOME_SECURITY_SLIDES.map((slide, index) => {
            const active = index === activeIndex;
            const ownsCopy = index === visibleCopyIndex;
            const showCopy = ownsCopy && copyShown;
            const collapsingCopy = ownsCopy && !copyShown && !active;
            return (
              <Panel
                key={slide.title}
                type="button"
                $active={active}
                aria-expanded={active}
                aria-label={slide.title}
                onClick={() => goTo(index)}
                onFocus={pause}
              >
                <PanelImage
                  src={slide.image}
                  alt={slide.imageAlt}
                  $active={active}
                  $slideIndex={index}
                />
                <PanelGradient $visible={showCopy} />
                <PanelScrim $active={active} />
                {ownsCopy && (
                  <PanelCopy
                    $visible={copyShown}
                    $collapsing={collapsingCopy}
                    aria-live={active && isPaused ? 'polite' : 'off'}
                  >
                    <HeadingH3 $onDark style={{ fontSize: 20 }}>
                      {slide.title}
                    </HeadingH3>
                    <BodyMd $onDark style={{ marginTop: TOKENS.space[8] }}>
                      {slide.description}
                    </BodyMd>
                  </PanelCopy>
                )}
              </Panel>
            );
          })}
        </DesktopPanels>
      </SectionContainer>
    </Section>
  );
};
